import React, { useMemo } from "react";
import { UsageMapType, UserMapType } from "../types";
import { formatDateYYYMMDD } from "../../helpers";
import { GetUsageQuery } from "../../generated";

type Props = {
  startDate: Date;
  endDate: Date;
  data: GetUsageQuery | undefined;
};

const useUsage = ({ startDate, endDate, data }: Props) => {
  const formatYAxis = (tick: any) => {
    if (tick >= 1_000_000) return `${(tick / 1_000_000).toFixed(2)}M`;
    if (tick >= 1_000) return `${(tick / 1_000).toFixed(1)}K`;
    return tick;
  };

  // Memoize the usage data processing
  const { usageData, monthlyUsers } = useMemo(() => {
    const usageMap: UsageMapType = {};
    const userMap: UserMapType = {};

    // Pre-fill dates
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      usageMap[formatDateYYYMMDD(d)] = 0;
    }

    // Process usage data if available
    if (data?.workspace?.usage?.totalUsage) {
      data.workspace.usage.totalUsage.forEach((entry: any) => {
        const dateStr = entry.date;
        usageMap[dateStr] =
          Number(usageMap[dateStr] || 0) + Number(entry.tokens);

        if (userMap[entry.userId]) {
          userMap[entry.userId].tokens =
            Number(userMap[entry.userId].tokens) + Number(entry.tokens);
        } else {
          userMap[entry.userId] = {
            firstName: entry.firstName,
            lastName: entry.lastName,
            tokens: Number(entry.tokens),
          };
        }
      });
    }

    return {
      usageData: Object.entries(usageMap).map(([date, tokens]) => ({
        date,
        tokens,
      })),
      monthlyUsers: Object.entries(userMap)
        .map(([userId, user]) => ({
          userId,
          ...user,
        }))
        .sort((a, b) => b.tokens - a.tokens),
    };
  }, [data?.workspace?.usage?.totalUsage]);

  const maxUsageValue = usageData
    ? Math.max(...usageData?.map((d: any) => Number(d.tokens) || 0))
    : 0;

  return {
    formatYAxis,
    usageData,
    monthlyUsers,
    maxUsageValue,
  };
};

export default useUsage;
